<template>
  <div
    ref="calendarContent"
    class="flex-grow-1"
    :class="{ pending }"
  >
    <div
      class="d-flex  align-items-end justify-content-end mb-2"
      style="margin-top: -10px"
    >
      <ReportCheckbox
        v-model="showWeekends"
        text="Weekend"
        class="checkbox-switch"
      />
    </div>

    <BModal
      v-model="showAddEventModal"
      hide-footer
      hide-header
      @hidden="selected = { day: '', hourId: '' }"
    >
      <CalendarRuleForm
        :catalog-id="catalogId"
        :init-day="selected.day"
        :init-hour="selected.hourId"
        :rule="selectedRule"
        @cancel="showAddEventModal = false"
        @refresh="request"
      />
    </BModal>
    <div
      @mouseleave="hover = { day: '', dietId: '', productId: '' }"
    >
      <div
        class="row no-gutters position-relative calendar-header"
        :style="{ marginRight: `${scrollbarWidth}px`}"
      >
        <div
          class="day-column"
          :style="{ width: `${columnWidth}%` }"
        >
          <div
            class="day-header"
          >
            Godzina
          </div>
        </div>
        <div
          class="position-relative"
          :style="{
            width: `${columnWidth*7}%`,
            overflow: animation > 0 ? 'hidden' : 'visible'
          }"
        >
          <div
            class="w-100"
          >
            <div
              class="position-absolute w-100 d-flex no-gutters"
            >
              <div
                v-for="d in days"
                :key="d"
                class="col day-column"
                :class="{ today: today === d }"
              >
                <div
                  class="day-header"
                  :class="{
                    highlight: selected.day === d,
                    hover: hover.day === d
                  }"
                  @mouseenter="hover = { day: d, dietId: '', productId: '' }"
                >
                  {{ formatWeekDay(d) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="row no-gutters mx-0 calendar flex-grow-1"
      >
        <div
          class="day-column"
          :style="{ width: `${columnWidth}%` }"
        >
          <div
            v-for="p in hours"
            :key="p.id"
            class="product-name"
            :style="{ height: `${rowHeight}px` }"
          >
            {{ p.name }}
          </div>
        </div>
        <div
          ref="calendarWrapper"
          class="calendar-wrapper"
          :style="{ width: `${columnWidth * 7}%` }"
        >
          <div
            class="w-100 h-100"
          >
            <div class="products-wrapper d-flex no-gutters">
              <div
                v-for="day in days"
                :key="day"
                class="col day-column position-relative"
                :class="{
                  today: today === day,
                  hover: day === hover.day,
                  highlight: day === selected.day
                }"
                :style="{ width: `${(1/7)*100}%` }"
              >
                <div
                  v-for="p in hours"
                  :key="p.id"
                  class="product-row content-cell"
                  :style="{ height: `${rowHeight}px` }"
                  :class="{
                    hover: hover.hourId === p.id,
                    highlight: selected.hourId === p.id
                  }"
                  @click.stop="toggleSelected({ day, hourId: p.id })"
                >
                    &nbsp;
                </div>

                <template v-if="!expandedDays[day]">
                  <div
                    v-for="item in collapsedDayItems[day]"
                    :key="item.id"
                    :class="item.type === 'summary' ? 'event-group-cell w-100' : 'event-cell w-100'"
                    :style="{ height: `${item.height}px`, top: `${item.top}px` }"
                    @click.stop="handleItemClick(item)"
                  >
                    <template v-if="item.type === 'summary'">
                      <div class="event-group-count">
                        {{ item.count }}
                      </div>
                      <div class="event-group-label">
                        {{ formatHour(item.minStart) }} - {{ formatHour(item.maxEnd) }}
                      </div>
                    </template>
                    <template v-else>
                      <div class="font-weight-bold">
                        {{ item.event.offer.name }}
                      </div>
                      <div>
                        {{ formatHour(item.event.startMinutes) }}
                        -
                        {{ formatHour(item.event.startMinutes + item.event.durationMinutes) }}
                      </div>
                    </template>
                  </div>
                </template>

                <template v-else>
                  <div
                    class="collapse-btn"
                    @click.stop="$set(expandedDays, day, false)"
                  >
                    ✕
                  </div>
                  <div
                    v-for="item in expandedDayItems[day]"
                    :key="item.id"
                    class="event-cell"
                    :style="{
                      height: `${item.height}px`,
                      top: `${item.top}px`,
                      left: `${item.left}%`,
                      width: `${item.width}%`
                    }"
                    @click.stop="selectedRule = item.event; showAddEventModal = true"
                  >
                    <div class="font-weight-bold">
                      {{ item.event.offer.name }}
                    </div>
                    <div>
                      {{ formatHour(item.event.startMinutes) }}
                      -
                      {{ formatHour(item.event.startMinutes + item.event.durationMinutes) }}
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReportCheckbox from '@/components/reports/ReportCheckbox';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import CalendarRuleForm from './calendar/CalendarRuleForm';

export default {
  props: {
    catalogId: String,
    nodeIds: Array,
  },
  data: () => ({
    today: moment().startOf('day').format(),
    calendar: {},
    updated: {},
    pending: false,
    aiPending: false,
    dates: null,
    moveDirection: '',
    rowHeight: 70,
    showAddEventModal: false,
    showUpload: false,
    rules: [],
    selected: {
      day: '',
      dietId: '',
      productId: '',
    },
    hover: {
      day: '',
      dietId: '',
      productId: '',
    },
    exportPending: false,
    showWeekends: false,
    animation: 0,
    wrapperHeight: 0,
    selectedRule: null,
    expandedDays: {},
    minHour: 6,
    showHours: 16,
  }),
  components: {
    ReportCheckbox,
    CalendarRuleForm,
  },
  computed: {
    ...mapGetters('shop', [
      'catalogDetails',
      'products',
      'listings',
    ]),
    parsedEvents() {
      const nodeIds = this.nodeIds.filter((x) => x.subCatalogId !== '#products').map((x) => x.nodeId);
      const productIds = this.nodeIds.filter((x) => x.subCatalogId === '#products').map((x) => x.nodeId);

      return this.rules.map((ev) => ({
        ...ev,
        offer: this.listings.find((x) => x.id === ev.offerId),
        day: moment().startOf('week').add(ev.weekDay === 0 ? 6 : ev.weekDay - 1, 'days').format(),
        top: this.calcTop(ev),
        height: this.calcHeight(ev),
      }))
        .filter((x) => x.offer)
        .filter((x) => productIds.length === 0 || x.offer.productIds.some((id) => productIds.includes(id)))
        .filter((x) => nodeIds.length === 0 || x.offer.assignments.some((a) => nodeIds.includes(a.nodeId)));
    },
    dayEventGroups() {
      return this.days.reduce((result, day) => {
        const dayEvents = this.parsedEvents.filter((x) => x.day === day);
        // eslint-disable-next-line no-param-reassign
        result[day] = this.buildOverlapGroups(dayEvents);
        return result;
      }, {});
    },
    collapsedDayItems() {
      return this.days.reduce((result, day) => {
        const items = [];
        this.dayEventGroups[day].forEach((group) => {
          if (group.events.length === 1) {
            const ev = group.events[0];
            items.push({
              type: 'event', id: ev.id, event: ev, top: ev.top, height: ev.height,
            });
          } else {
            items.push({
              type: 'summary',
              id: `group-${day}-${group.id}`,
              top: group.top,
              height: group.height,
              count: group.events.length,
              minStart: group.minStart,
              maxEnd: group.maxEnd,
              day,
            });
          }
        });
        // eslint-disable-next-line no-param-reassign
        result[day] = items;
        return result;
      }, {});
    },
    expandedDayItems() {
      return this.days.reduce((result, day) => {
        const items = [];
        this.dayEventGroups[day].forEach((group) => {
          const n = group.events.length;
          group.events.forEach((ev, idx) => {
            items.push({
              id: ev.id,
              event: ev,
              top: ev.top,
              height: ev.height,
              left: n > 1 ? (idx / n) * 100 : 0,
              width: n > 1 ? (1 / n) * 100 : 100,
            });
          });
        });
        // eslint-disable-next-line no-param-reassign
        result[day] = items;
        return result;
      }, {});
    },
    hours() {
      return Array.from({ length: this.showHours }, (_, i) => ({
        id: i + this.minHour,
        name: `${i + this.minHour}:00`,
      }));
    },
    updatedCount() {
      return Object.keys(this.updated).filter((x) => this.isUpdated(x)).length;
    },
    columnWidth() {
      return (1 / this.columnCount) * 100;
    },
    columnCount() {
      return 7 + 1;
    },
    days() {
      const start = moment().startOf('week');
      const days = [];

      while (start.isBefore(moment().startOf('week').add(1, 'week'))) {
        if (this.showWeekends || (start.day() !== 6 && start.day() !== 0)) {
          days.push(start.format());
        }
        start.add(1, 'day');
      }

      return days;
    },
    scrollbarWidth() {
      const scrollDiv = document.createElement('div');
      scrollDiv.className = 'scrollbar-measure';
      document.body.appendChild(scrollDiv);

      // Get the scrollbar width
      const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;

      // Delete the div
      document.body.removeChild(scrollDiv);
      return scrollbarWidth;
    },
  },
  methods: {
    ...mapActions('shop', [
      'getCatalogListings',
      'getCalendarRules',
    ]),
    buildOverlapGroups(events) {
      if (events.length === 0) return [];
      const sorted = [...events].sort((a, b) => a.startMinutes - b.startMinutes);
      const groups = [];
      let current = [sorted[0]];
      let end = sorted[0].startMinutes + sorted[0].durationMinutes;

      sorted.slice(1).forEach((ev) => {
        if (ev.startMinutes < end) {
          current.push(ev);
          end = Math.max(end, ev.startMinutes + ev.durationMinutes);
        } else {
          groups.push(current);
          current = [ev];
          end = ev.startMinutes + ev.durationMinutes;
        }
      });
      groups.push(current);

      return groups.map((g, i) => {
        const minStart = Math.min(...g.map((e) => e.startMinutes));
        const maxEnd = Math.max(...g.map((e) => e.startMinutes + e.durationMinutes));
        return {
          id: i,
          events: g,
          minStart,
          maxEnd,
          top: (this.wrapperHeight / (24 * 60)) * minStart,
          height: (this.wrapperHeight / (24 * 60)) * (maxEnd - minStart),
        };
      });
    },
    handleItemClick(item) {
      if (item.type === 'summary') {
        this.$set(this.expandedDays, item.day, true);
      } else {
        this.selectedRule = item.event;
        this.showAddEventModal = true;
      }
    },
    formatHour(minutes) {
      const hour = Math.floor(minutes / 60).toString().padStart(2, '0');
      const minute = (minutes % 60).toString().padStart(2, '0');
      return `${hour}:${minute}`;
    },
    calcHeight({ durationMinutes }) {
      return (this.wrapperHeight / (this.showHours * 60)) * durationMinutes;
    },
    calcTop({ startMinutes }) {
      const minuteHeight = (this.wrapperHeight / (this.showHours * 60));
      return minuteHeight * (startMinutes - this.minHour * 60);
    },
    importData(entries) {
      entries.forEach((entry) => {
        this.$set(this.updated, this.getKey(entry.day, entry.dietId, entry.productId), entry);
      });
    },
    isEmpty(o) {
      return o === null || o === undefined
        || (o.title === '' && o.description === '');
    },
    isUpdated(key) {
      if (this.isEmpty(this.calendar[key])) {
        return !this.isEmpty(this.updated[key]);
      }

      return !!this.updated[key]
      && (this.updated[key].title !== this.calendar[key].title
      || this.updated[key].mealId !== this.calendar[key].mealId);
    },
    toggleSelected(cell) {
      this.selected = cell;
      this.selectedRule = null;

      this.showAddEventModal = true;
    },
    dayValue(key) {
      return this.updated[key] || this.calendar[key];
    },
    getKey(day, dietNodeId, productId) {
      if (typeof day === 'object' && day !== null) {
        return `${moment(day.day).format('YYYY-MM-DD')}_${day.hourId}`;
      }
      return `${moment(day).format('YYYY-MM-DD')}_${dietNodeId}_${productId}`;
    },
    formatWeekDay(date) {
      return moment(date).format('ddd');
    },
    formatDay(date) {
      return moment(date).format('D');
    },
    request() {
      this.pending = true;

      this.getCalendarRules({
        params: {
          catalogId: this.catalogId,
        },
      })
        .then(({ data }) => {
          this.rules = data;
        })
        .finally(() => {
          this.pending = false;
        });
    },
  },
  created() {
    this.request();
  },
  mounted() {
    this.wrapperHeight = this.$refs.calendarWrapper.clientHeight;
  },
};
</script>

<style lang="scss" scoped>
$rowHeight: 70px;
$hover-bg: rgba(#aaa, 0.06);
$highlight-bg: rgba($orange, 0.1);
$border-light: #ececec;
$border-lighter: #eee;
$border-medium: #dcdcdc;
$today-bg: rgba($blue, 0.04);
$today-content-bg: rgba(#fff, 0.03);

.forward-enter-active,
.forward-leave-active,
.back-enter-active,
.back-leave-active {
  transition: all 0.4s ease;
  position: absolute;
  z-index: 0;
}

.event-cell {
  position: absolute;
  border: 1px solid rgba($blue, 0.4);
  padding: 5px;
  border-radius: 5px;
  font-size: 11px;
  cursor: pointer;
  overflow: hidden;
  box-sizing: border-box;
  background-color: #fff;

  &:hover {
    background-color: rgba($blue, 0.1);
  }
}

.event-group-cell {
  position: absolute;
  border: 2px dashed rgba($blue, 0.35);
  border-radius: 5px;
  background-color: rgba($blue, 0.06);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  transition: background-color 150ms ease;

  &:hover {
    background-color: rgba($blue, 0.14);
  }
}

.event-group-count {
  font-size: 18px;
  font-weight: 700;
  color: $blue;
  line-height: 1;
}

.event-group-label {
  color: #888;
  margin-top: 2px;
}

.collapse-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 10;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(#000, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 9px;
  line-height: 1;

  &:hover {
    background: rgba(#000, 0.18);
  }
}

.day-textarea {
  border: none;
  resize: none;
  width: 100%;
  height: 100%;
  font-weight: 300;
}

.calendar-header {
  border-top: 1px solid #ececec;
  margin-top: 5px;
}

.content-cell {
  position: relative;
  cursor: pointer;
  border: 1px solid transparent;
  overflow: auto;

  textarea {
    border: 1px solid transparent;
  }

  &.updated {
    border-color: $orange;
  }

  &:hover {
    background-color: $hover-bg;
  }

  &.hover {
    background-color: $hover-bg;
  }
  &.highlight {
    background-color: $highlight-bg;
  }
}

.calendar-wrapper {
  overflow: hidden;
  position: relative;
}

.product-row {
  border-bottom: 1px solid $border-lighter;
  height: $rowHeight;
  font-size: 12px;

  &.hover {
    .content-cell {
      background-color: $hover-bg;
    }
  }

  &.highlight {
    .content-cell {
      background-color: $highlight-bg;
    }
  }
}

.product-name {
  font-weight: 500;
  font-size: 0.8rem;
  word-wrap: break-word;
  padding: 10px;
  border-bottom: 1px solid $border-light;
}

.products-wrapper {
  width: 100%;
  position: relative;
  z-index: 2;
}

.diet-name {
  height: $rowHeight;
  font-size: 12px;
  padding: 5px;
  word-wrap: break-word;
  border-bottom: 1px solid $border-lighter;
  font-weight: 400;

  &.hover, &:hover {
    background-color: $hover-bg;
  }

  &.highlight {
    background-color: $highlight-bg;
  }
}

.secondary-icon {
  font-size: 14px;
}

.day-column {
  position: relative;

  .remove-tooltip {
    position: absolute;
    z-index: 100;
    bottom: 0;
    opacity: 0;
    width: 100%;
    padding: 2px;
    text-align: center;
    height: 35px;
    transition: opacity 200ms ease, bottom 200ms ease;
  }

  &:hover {
    .remove-tooltip {
      display: block;
      bottom: -35px;
      opacity: 1;
    }
  }
}

.forward-enter-active,
.forward-leave-active,
.back-enter-active,
.back-leave-active {
  .calendar-header {
    overflow: hidden;
  }
}

.forward-enter,
.back-leave-to {
  transform: translateX(100%);
}

.forward-enter-to,
.back-enter-to {
  transform: translateX(0%);
}

.forward-leave-to,
.back-enter {
  transform: translateX(-100%);
}

.pending {
  opacity: 0.8;
}

.day-header {
  text-align: center;
  position: sticky;
  border-bottom: 1px solid $border-medium;
  padding: 10px;
  font-size: 0.8rem;
  font-weight: 400;
  text-transform: uppercase;
  height: 71px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .day-number {
    font-size: 20px;
  }

  &.highlight {
    background-color: $highlight-bg;
  }

  &:hover, &.hover {
    background-color: $hover-bg;
  }
}

.calendar {
  overflow-y: scroll;
  height: calc(100vh - 310px);
  padding-bottom: 20px;
}

.today {
  background-color: $today-bg;

  textarea {
    background-color: $today-content-bg;
  }

  .day-header {
    color: $blue;
  }
}

.day-column {
  border-right: 1px solid $border-medium;
}
</style>
